import {
  ApiListResponse,
  ApiSingleResponse,
  PhotoType,
  ServicePhoto,
  ServiceRecord,
  ServiceRecordCreateDto,
  ServiceRecordPatchDto,
  ServiceRecordStatusFilter,
} from './types';

// Rozróżnienie adresów API dla środowisk Production (crm.madkum.pl) i Staging (staging.pages.dev):
const getBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    if (window.location.hostname === 'crm.madkum.pl' || window.location.hostname === 'madkum-crm-web.pages.dev') {
      return '';
    }
    if (window.location.hostname.includes('staging')) {
      return 'https://madkum-crm-api-staging.marcin-ed0.workers.dev';
    }
  }
  return '';
};

export class ApiErrorCustom extends Error {
  code: string;
  requestId?: string;
  status: number;

  constructor(code: string, message: string, status: number, requestId?: string) {
    super(message);
    this.name = 'ApiErrorCustom';
    this.code = code;
    this.status = status;
    this.requestId = requestId;
  }
}

export function resetApiAccessGuard(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem('api_access_attempts');
    sessionStorage.removeItem('api_access_last_ts');
  } catch {
    // Ignorujemy błędy braku dostępu do sessionStorage
  }
}

export function shouldTriggerApiAccessRedirect(): boolean {
  if (typeof window === 'undefined') return false;

  const now = Date.now();
  const lastTs = parseInt(sessionStorage.getItem('api_access_last_ts') || '0', 10);
  let attempts = parseInt(sessionStorage.getItem('api_access_attempts') || '0', 10);

  // Jeśli ostatnia próba była ponad 15 sekund temu (użytkownik opuścił stronę/cofnął i wrócił), resetujemy licznik
  if (now - lastTs > 15000) {
    attempts = 0;
  }

  // Maksymalnie 2 automatyczne próby przekierowania w krótkim oknie
  if (attempts >= 2) {
    return false;
  }

  sessionStorage.setItem('api_access_attempts', (attempts + 1).toString());
  sessionStorage.setItem('api_access_last_ts', now.toString());
  return true;
}

export function handleCloudflareAccessChallengeIfNeeded(response: Response, responseText: string): void {
  const contentType = response.headers.get('content-type') || '';
  const isAccessChallenge =
    contentType.includes('text/html') ||
    responseText.includes('Sign in to Cloudflare Access') ||
    responseText.includes('access.cloudflare.com');

  if (isAccessChallenge && typeof window !== 'undefined') {
    const isProductionSameOrigin =
      (window.location.hostname === 'crm.madkum.pl' || window.location.hostname === 'madkum-crm-web.pages.dev');
    if (isProductionSameOrigin) {
      if (shouldTriggerApiAccessRedirect()) {
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `/api/v1/health?redirect=${encodeURIComponent(currentPath)}`;
        throw new ApiErrorCustom('AUTH_INITIALIZING', 'Łączenie z CRM...', 202);
      }
    }
  }
}

export function handleFetchTypeErrorIfNeeded(err: unknown, urlPath: string): void {
  if (typeof window === 'undefined') return;

  const isProductionSameOrigin =
    (window.location.hostname === 'crm.madkum.pl' || window.location.hostname === 'madkum-crm-web.pages.dev') &&
    urlPath.startsWith('/api/v1/');

  if (!isProductionSameOrigin) return;

  const isTypeError = err instanceof TypeError || (err && (err as any).name === 'TypeError');
  if (isTypeError) {
    if (shouldTriggerApiAccessRedirect()) {
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/api/v1/health?redirect=${encodeURIComponent(currentPath)}`;
      throw new ApiErrorCustom('AUTH_INITIALIZING', 'Łączenie z CRM...', 202);
    }
  }
}

async function safeFetch(url: string, init?: RequestInit): Promise<Response> {
  try {
    const response = await fetch(url, init);
    let responseText = '';
    try {
      responseText = await response.clone().text();
    } catch {
      // Ignorujemy błąd odczytu surowego tekstu z klona
    }
    handleCloudflareAccessChallengeIfNeeded(response, responseText);
    return response;
  } catch (err: unknown) {
    let urlPath = url;
    try {
      const urlObj = new URL(url, typeof window !== 'undefined' ? window.location.href : 'http://localhost');
      urlPath = urlObj.pathname;
    } catch {
      // Ignorujemy błąd parsowania URL
    }
    handleFetchTypeErrorIfNeeded(err, urlPath);
    throw err;
  }
}

export async function fetchRecords(params?: {
  search?: string;
  status?: ServiceRecordStatusFilter;
  showArchived?: boolean;
  limit?: number;
  offset?: number;
}): Promise<ApiListResponse> {
  const baseUrl = getBaseUrl();
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  if (params?.status) {
    if (params.status === 'ACTIVE') {
      if (!params.showArchived) {
        query.set('status', 'ACTIVE');
      }
      // If showArchived is true in ACTIVE mode, don't set status parameter so backend returns all records
    } else if (params.status !== 'ALL') {
      query.set('status', params.status);
    }
  }
  if (params?.search && params.search.trim().length > 0) query.set('search', params.search.trim());

  const url = `${baseUrl}/api/v1/records?${query.toString()}`;
  const response = await safeFetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
    credentials: 'same-origin',
  });

  const responseText = await response.text();

  let json: any = {};
  try {
    json = JSON.parse(responseText);
  } catch (e) {
    // Ignorujemy błąd parsowania JSON
  }

  if (!response.ok) {
    const errorObj = json?.error;
    const code = errorObj?.code || `HTTP_${response.status}`;
    const message = errorObj?.message || `Błąd serwera (${response.status})`;
    const requestId = errorObj?.requestId || response.headers.get('X-Request-ID') || undefined;
    throw new ApiErrorCustom(code, message, response.status, requestId);
  }

  return json as ApiListResponse;
}

export async function fetchRecordByUuid(uuid: string): Promise<ServiceRecord> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/api/v1/records/${encodeURIComponent(uuid)}`;
  const response = await safeFetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
    credentials: 'same-origin',
  });

  const responseText = await response.text();

  let json: any = {};
  try {
    json = JSON.parse(responseText);
  } catch (e) {
    // Ignorujemy błąd parsowania JSON
  }

  if (!response.ok) {
    const errorObj = json?.error;
    const code = errorObj?.code || `HTTP_${response.status}`;
    const message = errorObj?.message || `Nie znaleziono zgłoszenia (${response.status})`;
    const requestId = errorObj?.requestId || response.headers.get('X-Request-ID') || undefined;
    throw new ApiErrorCustom(code, message, response.status, requestId);
  }

  const resData = json as ApiSingleResponse;
  return resData.data;
}

export async function updateRecord(uuid: string, patchDto: ServiceRecordPatchDto): Promise<ServiceRecord> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/api/v1/records/${encodeURIComponent(uuid)}`;
  const response = await safeFetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'If-Match': `"${patchDto.expectedRevision}"`,
    },
    body: JSON.stringify(patchDto),
    credentials: 'same-origin',
  });

  const responseText = await response.text();

  let json: any = {};
  try {
    json = JSON.parse(responseText);
  } catch (e) {
    // Ignorujemy błąd parsowania JSON
  }

  if (!response.ok) {
    const errorObj = json?.error;
    const code = errorObj?.code || `HTTP_${response.status}`;
    const message = errorObj?.message || `Błąd aktualizacji zgłoszenia (${response.status})`;
    const requestId = errorObj?.requestId || response.headers.get('X-Request-ID') || undefined;
    throw new ApiErrorCustom(code, message, response.status, requestId);
  }

  const resData = json as ApiSingleResponse;
  return resData.data;
}

export async function createRecord(createDto: ServiceRecordCreateDto, idempotencyKey?: string): Promise<ServiceRecord> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/api/v1/records`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
    headers['X-Idempotency-Key'] = idempotencyKey;
  }

  const response = await safeFetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(createDto),
    credentials: 'same-origin',
  });

  const responseText = await response.text();

  let json: any = {};
  try {
    json = JSON.parse(responseText);
  } catch (e) {
    // Ignorujemy błąd parsowania JSON
  }

  if (!response.ok) {
    const errorObj = json?.error;
    const code = errorObj?.code || `HTTP_${response.status}`;
    const message = errorObj?.message || `Błąd tworzenia zgłoszenia (${response.status})`;
    const requestId = errorObj?.requestId || response.headers.get('X-Request-ID') || undefined;
    throw new ApiErrorCustom(code, message, response.status, requestId);
  }

  const resData = json as ApiSingleResponse;
  return resData.data;
}

export function getPhotoBinaryUrl(recordUuid: string, photoId: string): string {
  const baseUrl = getBaseUrl();
  return `${baseUrl}/api/v1/records/${encodeURIComponent(recordUuid)}/photos/${encodeURIComponent(photoId)}`;
}

export async function fetchRecordPhotos(recordUuid: string): Promise<ServicePhoto[]> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/api/v1/records/${encodeURIComponent(recordUuid)}/photos`;
  const response = await safeFetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
    credentials: 'same-origin',
  });

  const responseText = await response.text();
  let json: any = {};
  try {
    json = JSON.parse(responseText);
  } catch (e) {
    // Ignorujemy błąd parsowania
  }

  if (!response.ok) {
    const errorObj = json?.error;
    const code = errorObj?.code || `HTTP_${response.status}`;
    const message = errorObj?.message || `Błąd pobierania zdjęć (${response.status})`;
    const requestId = errorObj?.requestId || response.headers.get('X-Request-ID') || undefined;
    throw new ApiErrorCustom(code, message, response.status, requestId);
  }

  return (json.photos || []) as ServicePhoto[];
}

export async function uploadRecordPhoto(
  recordUuid: string,
  file: File,
  photoType: PhotoType,
  isPrimary: boolean,
  idempotencyKey: string
): Promise<ServicePhoto> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/api/v1/records/${encodeURIComponent(recordUuid)}/photos`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('photoType', photoType);
  if (isPrimary) {
    formData.append('isPrimary', 'true');
  }

  const response = await safeFetch(url, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'X-Idempotency-Key': idempotencyKey,
    },
    body: formData,
    credentials: 'same-origin',
  });

  const responseText = await response.text();
  let json: any = {};
  try {
    json = JSON.parse(responseText);
  } catch (e) {
    // Ignorujemy błąd parsowania
  }

  if (!response.ok) {
    const errorObj = json?.error;
    const code = errorObj?.code || `HTTP_${response.status}`;
    const message = errorObj?.message || `Błąd wysyłania zdjęcia (${response.status})`;
    const requestId = errorObj?.requestId || response.headers.get('X-Request-ID') || undefined;
    throw new ApiErrorCustom(code, message, response.status, requestId);
  }

  return json as ServicePhoto;
}

export async function deleteRecordPhoto(recordUuid: string, photoId: string): Promise<void> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/api/v1/records/${encodeURIComponent(recordUuid)}/photos/${encodeURIComponent(photoId)}`;
  const response = await safeFetch(url, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
    },
    credentials: 'same-origin',
  });

  const responseText = await response.text();
  let json: any = {};
  try {
    json = JSON.parse(responseText);
  } catch (e) {
    // Ignorujemy błąd parsowania
  }

  if (!response.ok) {
    const errorObj = json?.error;
    const code = errorObj?.code || `HTTP_${response.status}`;
    const message = errorObj?.message || `Błąd usuwania zdjęcia (${response.status})`;
    const requestId = errorObj?.requestId || response.headers.get('X-Request-ID') || undefined;
    throw new ApiErrorCustom(code, message, response.status, requestId);
  }
}

export async function fetchRecordCommunications(recordUuid: string): Promise<import('./types').RecordCommunication[]> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/api/v1/records/${encodeURIComponent(recordUuid)}/communications`;
  const response = await safeFetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
    credentials: 'same-origin',
  });

  const responseText = await response.text();
  let json: any = {};
  try {
    json = JSON.parse(responseText);
  } catch (e) {
    // Ignorujemy błąd parsowania
  }

  if (!response.ok) {
    const errorObj = json?.error;
    const code = errorObj?.code || `HTTP_${response.status}`;
    const message = errorObj?.message || `Błąd pobierania historii komunikacji (${response.status})`;
    const requestId = errorObj?.requestId || response.headers.get('X-Request-ID') || undefined;
    throw new ApiErrorCustom(code, message, response.status, requestId);
  }

  return (json.data || []) as import('./types').RecordCommunication[];
}

export async function postRecordCommunication(
  recordUuid: string,
  payload: {
    type: import('./types').CommunicationType;
    channel: import('./types').CommunicationChannel;
    action: import('./types').CommunicationAction;
    source: import('./types').CommunicationSource;
    content: string;
    idempotencyKey?: string;
  }
): Promise<import('./types').RecordCommunication> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/api/v1/records/${encodeURIComponent(recordUuid)}/communications`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  if (payload.idempotencyKey) {
    headers['X-Idempotency-Key'] = payload.idempotencyKey;
  }

  const response = await safeFetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    credentials: 'same-origin',
  });

  const responseText = await response.text();
  let json: any = {};
  try {
    json = JSON.parse(responseText);
  } catch (e) {
    // Ignorujemy błąd parsowania
  }

  if (!response.ok) {
    const errorObj = json?.error;
    const code = errorObj?.code || `HTTP_${response.status}`;
    const message = errorObj?.message || `Błąd zapisu historii komunikacji (${response.status})`;
    const requestId = errorObj?.requestId || response.headers.get('X-Request-ID') || undefined;
    throw new ApiErrorCustom(code, message, response.status, requestId);
  }

  return json.data as import('./types').RecordCommunication;
}

export async function deleteCommunication(commUuid: string): Promise<{ success: boolean; data: any }> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/api/v1/communications/${encodeURIComponent(commUuid)}`;
  const response = await safeFetch(url, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
    },
    credentials: 'same-origin',
  });

  const responseText = await response.text();
  let json: any = {};
  try {
    json = JSON.parse(responseText);
  } catch (e) {}

  if (!response.ok) {
    const errorObj = json?.error;
    const code = errorObj?.code || `HTTP_${response.status}`;
    const message = errorObj?.message || `Błąd usuwania wpisu z historii (${response.status})`;
    const requestId = errorObj?.requestId || response.headers.get('X-Request-ID') || undefined;
    throw new ApiErrorCustom(code, message, response.status, requestId);
  }

  return json;
}

export async function restoreCommunication(commUuid: string): Promise<import('./types').RecordCommunication> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/api/v1/communications/${encodeURIComponent(commUuid)}/restore`;
  const response = await safeFetch(url, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
    },
    credentials: 'same-origin',
  });

  const responseText = await response.text();
  let json: any = {};
  try {
    json = JSON.parse(responseText);
  } catch (e) {}

  if (!response.ok) {
    const errorObj = json?.error;
    const code = errorObj?.code || `HTTP_${response.status}`;
    const message = errorObj?.message || `Błąd przywracania wpisu z historii (${response.status})`;
    const requestId = errorObj?.requestId || response.headers.get('X-Request-ID') || undefined;
    throw new ApiErrorCustom(code, message, response.status, requestId);
  }

  return json.data as import('./types').RecordCommunication;
}

export async function patchCommunication(commUuid: string, content: string): Promise<import('./types').RecordCommunication> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/api/v1/communications/${encodeURIComponent(commUuid)}`;
  const response = await safeFetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ content }),
    credentials: 'same-origin',
  });

  const responseText = await response.text();
  let json: any = {};
  try {
    json = JSON.parse(responseText);
  } catch (e) {}

  if (!response.ok) {
    const errorObj = json?.error;
    const code = errorObj?.code || `HTTP_${response.status}`;
    const message = errorObj?.message || `Błąd edycji notatki (${response.status})`;
    const requestId = errorObj?.requestId || response.headers.get('X-Request-ID') || undefined;
    throw new ApiErrorCustom(code, message, response.status, requestId);
  }

  return json.data as import('./types').RecordCommunication;
}


